import React from 'react';
import ReactDOM from 'react-dom/client';

/**
 * 确认弹窗的 props 定义
 * - 与 antd-mobile Dialog.confirm 的入参保持兼容（title/content/confirmText/cancelText）
 * - 额外支持 icon（顶部圆形图标）与 tone（强调色，默认红色危险态）
 */
export interface ConfirmDialogOptions {
  title: React.ReactNode;
  content?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  /** 顶部圆形图标内容，默认垃圾桶 emoji 风格图标 */
  icon?: React.ReactNode;
  /** 强调色调，默认 danger（红色） */
  tone?: 'danger' | 'primary';
}

// 项目品牌主色（参考 ProfileWhatsAppAccounts/styles.ts 紫色梯度）
const BRAND_PURPLE = '#6f6cf6';

// 危险红（参考样式表中已有 #ef6b6b / #ff6b6b）
const DANGER_RED = '#ef4444';

// 亮白卡片底色与分隔线（参考产品现有亮色卡片）
const CARD_BG = '#ffffff';
const DIVIDER = 'rgba(15,23,42,0.08)';

/**
 * 默认垃圾桶图标 SVG（与设计稿一致的细线条风格）
 */
const DefaultTrashIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * 内部弹窗组件
 * - 受控 visible，关闭后通过外部 onResult 回传结果
 */
const ConfirmDialogInner: React.FC<{
  options: ConfirmDialogOptions;
  onResult: (confirmed: boolean) => void;
}> = ({ options, onResult }) => {
  const tone = options.tone ?? 'danger';
  const accent = tone === 'danger' ? DANGER_RED : BRAND_PURPLE;
  const iconNode = options.icon ?? <DefaultTrashIcon color={accent} />;

  // 遮罩层样式（深色磨砂背景）
  const maskStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    animation: 'cm-fade-in 180ms ease-out',
  };

  // 卡片样式
  const cardStyle: React.CSSProperties = {
    width: 296,
    maxWidth: 'calc(100vw - 48px)',
    background: CARD_BG,
    borderRadius: 18,
    boxShadow: '0 18px 48px rgba(15,23,42,0.18)',
    overflow: 'hidden',
    color: '#1f2937',
    animation: 'cm-pop-in 220ms cubic-bezier(0.2, 0.9, 0.3, 1.2)',
  };

  // 顶部图标圆形（居中）
  const iconWrapStyle: React.CSSProperties = {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: `${accent}14`, // 8% 透明度填充
    border: `1px solid ${accent}40`,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: accent,
    margin: '28px auto 16px', // 上下间距 + auto 水平居中
  };

  // 图标容器外层 wrapper：保证 inline-flex 也能居中
  const iconRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
  };

  // 标题样式
  const titleStyle: React.CSSProperties = {
    fontSize: 17,
    fontWeight: 700,
    textAlign: 'center',
    margin: '0 24px 10px',
    color: '#111827',
  };

  // 描述样式
  const contentStyle: React.CSSProperties = {
    fontSize: 13,
    lineHeight: 1.7,
    textAlign: 'center',
    color: '#6b7280',
    margin: '0 24px 22px',
  };

  // 底部按钮容器（左右等分）
  const footerStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    borderTop: `1px solid ${DIVIDER}`,
  };

  // 共享按钮样式
  const buttonBaseStyle: React.CSSProperties = {
    height: 50,
    background: 'transparent',
    border: 'none',
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 120ms ease',
  };

  return (
    <div style={maskStyle} onClick={() => onResult(false)}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        {/* 居中包裹图标，避免 inline-flex 受文本对齐影响 */}
        <div style={iconRowStyle}>
          <div style={iconWrapStyle}>{iconNode}</div>
        </div>
        <div style={titleStyle}>{options.title}</div>
        {options.content && <div style={contentStyle}>{options.content}</div>}
        <div style={footerStyle}>
          {/* 取消按钮（左侧，浅灰文字） */}
          <button
            type="button"
            style={{ ...buttonBaseStyle, color: '#6b7280', borderRight: `1px solid ${DIVIDER}` }}
            onClick={() => onResult(false)}
          >
            {options.cancelText ?? '取消'}
          </button>
          {/* 确认按钮（右侧，强调色文字） */}
          <button
            type="button"
            style={{ ...buttonBaseStyle, color: accent, fontWeight: 600 }}
            onClick={() => onResult(true)}
          >
            {options.confirmText ?? '确认'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * 注入一次全局动画 keyframes（避免依赖 styled-components）
 */
let stylesInjected = false;
const injectStylesOnce = () => {
  if (stylesInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes cm-fade-in { from { opacity: 0 } to { opacity: 1 } }
    @keyframes cm-pop-in {
      from { opacity: 0; transform: scale(0.92) }
      to { opacity: 1; transform: scale(1) }
    }
  `;
  document.head.appendChild(style);
  stylesInjected = true;
};

/**
 * 暗色风格确认弹窗（imperative API）
 * 用法：
 *   const ok = await confirmDialog({ title: '...', content: '...' });
 *   if (!ok) return;
 */
export function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  injectStylesOnce();

  return new Promise<boolean>((resolve) => {
    // 创建独立挂载节点，避免污染 #root
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = ReactDOM.createRoot(container);

    // 卸载并清理 DOM 节点
    const cleanup = () => {
      root.unmount();
      if (container.parentNode) {
        container.parentNode.removeChild(container);
      }
    };

    const handleResult = (confirmed: boolean) => {
      cleanup();
      resolve(confirmed);
    };

    root.render(<ConfirmDialogInner options={options} onResult={handleResult} />);
  });
}
